import type { ProfileFieldConfig } from '../types/profile'
import type { Identity } from '../types/auth'
import type { Role } from '../types/identity'

export interface CompletionOutcome {
  /** Whole percent, derived — never a stored or written figure. */
  percent: number
  filled: string[]
  missing: ProfileFieldConfig[]
  /** The first thing worth doing next, or null when nothing is outstanding. */
  next: ProfileFieldConfig | null
  counted: number
}

function hasValue(identity: Identity, field: ProfileFieldConfig): boolean {
  switch (field.id) {
    case 'bio':
      return Boolean(identity.bio && identity.bio.trim())
    case 'location':
      return Boolean(identity.location && identity.location.trim())
    case 'interests':
      return (identity.interests ?? []).length > 0
    case 'skills':
      return (identity.skills ?? []).length > 0
    case 'lookingFor':
      return (identity.lookingFor ?? []).length > 0
    case 'languages':
      return (identity.languages ?? []).length > 0
    default:
      return false
  }
}

/**
 * Profile completion, calculated.
 *
 * The percentage is derived from the registered fields that apply to this
 * person's role, so a field added to the register is counted from that moment
 * and no number is ever asserted. Required fields carry twice the weight of
 * optional ones, because "no bio" and "no languages" are not equally incomplete.
 *
 * Deliberately never reaches 100% by rounding: a profile with something
 * outstanding reports at most 99%, so the figure cannot say "finished" while the
 * list below it still has work in it.
 */
export function calculateProfileCompletion(
  identity: Identity,
  fields: ProfileFieldConfig[],
  role: Role,
): CompletionOutcome {
  const applicable = fields.filter((field) => field.roles.length === 0 || field.roles.includes(role))
  const weight = (field: ProfileFieldConfig) => (field.required ? 2 : 1)
  const total = applicable.reduce((sum, field) => sum + weight(field), 0)

  const filled: string[] = []
  const missing: ProfileFieldConfig[] = []
  let earned = 0

  applicable.forEach((field) => {
    if (hasValue(identity, field)) {
      filled.push(field.id)
      earned += weight(field)
    } else {
      missing.push(field)
    }
  })

  const raw = total === 0 ? 100 : (earned / total) * 100
  const percent = missing.length === 0 ? 100 : Math.min(99, Math.round(raw))

  // Required fields first, so "complete profile" leads somewhere that matters.
  const next =
    missing.find((field) => field.required) ?? (missing.length > 0 ? missing[0] : null)

  return { percent, filled, missing, next, counted: applicable.length }
}
